import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import Page from 'contexts/page'
import { useIntegrations } from 'contexts/integrations'
import metas from 'pages/integrations/pages/integration/data'
import ModuleTitle from 'components/module-title'
import Filter from './filter'
import { IntegrationsWrapper } from './s'
import Paragraph from 'components/typography/paragraph'
import Integrations from 'pages/integrations/lists/integrations'
import HeroActions from 'components/hero-actions'
import { Generic as Empty } from 'components/empty'

const IntegrationsPage = ({ upgradable, ...props }) => {
    const { integrations } = useIntegrations()

    const [filter, setFilter] = useState({ status: 'any' })
    const [enriched, setEnriched] = useState(enrich(integrations))
    const [filtered, setFiltered] = useState(enriched)

    useEffect(() => {
        setEnriched(enrich(integrations))
    }, [integrations])

    useEffect(() => {
        setFiltered(() => {
            let filtered = enriched.filter(({ meta, categories, instances = [] }) => {
                if(filter.search?.length > 1) {
                    if(!meta.name.toLowerCase().includes(filter.search.toLowerCase()) && !instances.some(({ name }) => name.toLowerCase().includes(filter.search.toLowerCase()))) {
                        return false
                    }
                }

                if(!!filter.status && filter.status !== 'any') {
                    const status = instances.find(({ status }) => status === filter.status)?.status

                    if(filter.status !== status) {
                        return false
                    }
                }

                if(!!filter.country?.length) {
                    // Don’t return integrations with an explicit country association if the filter is set to none
                    if(filter.country.length === 1 && filter.country[0] === 'none' && !!meta.country) {
                        return false
                    }

                    if(!!meta.country && !filter.country.includes(meta.country)) {
                        return false
                    }
                }

                if(!!filter.category?.length) {
                    if(!filter.category.some(category => categories.includes(category))) {
                        return false
                    }
                }

                return true
            })

            if(filter.search?.length > 1) {
                filtered = filtered.sort((one, two) => {
                    const oneName = one.meta.name.toLowerCase()
                    const twoName = two.meta.name.toLowerCase()

                    if(oneName.startsWith(filter.search.toLowerCase())) {
                        return -1
                    }

                    if(twoName.startsWith(filter.search.toLowerCase())) {
                        return 1
                    }

                    return oneName.localeCompare(twoName)
                })
            }

            return filtered
        })
    }, [enriched.map(({ type }) => type).join('+'), JSON.stringify(filter)])

    const connectedIntegrations = filtered?.filter(({ instances = [] }) => !!instances.length)
    const availableIntegrations = filtered?.filter(({ instances = [] }) => !instances.length)

    return (
        <Page
            view="integrations_list"
            title={{
                id: 'noun_integrations',
                defaultMessage: 'Integrations'
            }}
            upgrade={{
                enabled: upgradable,
                passthrough: true
            }}>
            <ModuleTitle
                which="integrations"
                quickGuide={false}
                className="spacious" />
            <HeroActions>
                <Filter
                    filter={filter}
                    setFilter={setFilter}
                    items={enriched} />
            </HeroActions>
            <IntegrationsWrapper>
                {!!connectedIntegrations?.length && (
                    <div>
                        <Paragraph className="caption">
                            <FormattedMessage
                                id="integrations_connected"
                                defaultMessage="Your connections" />
                        </Paragraph>
                        <Integrations
                            {...props}
                            integrations={connectedIntegrations}
                            upgradable={upgradable} />
                    </div>

                )}
                {!!availableIntegrations?.length && (
                    <div>
                        {!!connectedIntegrations?.length && (
                            <Paragraph className="caption">
                                <FormattedMessage
                                    id="integrations_available"
                                    defaultMessage="Available integrations" />
                            </Paragraph>
                        )}
                        <Integrations
                            {...props}
                            integrations={availableIntegrations}
                            upgradable={upgradable} />
                    </div>
                )}
            </IntegrationsWrapper>
            {!filtered.length && <Empty />}
        </Page>
    )
}

const enrich = integrations => integrations.map(integration => {
    const meta = metas[integration.type]?.()

    return {
        ...integration,
        meta
    }
})

export default IntegrationsPage